<template>
  <component :is="tag" :class="buttonClasses" :disabled="loading || disabled" :type="type" v-bind="$attrs"
    @click="handleClick">
    <!-- Loading Spinner -->
    <BaseLoading v-if="loading" variant="spinner" :size="loadingSize" :color="loadingColor" class="mr-2" />

    <!-- Icon (before text) - String name -->
    <BaseIcon v-if="icon && typeof icon === 'string' && !iconAfter && !loading" :name="icon" :size="iconSize"
      :class="iconClasses" />

    <!-- Icon (before text) - Vue component -->
    <component v-else-if="icon && typeof icon !== 'string' && !iconAfter && !loading" :is="icon"
      :class="[iconClasses, iconSizeClasses]" />

    <!-- Icon Slot -->
    <slot v-if="!loading" name="icon" />

    <!-- Default Slot (text content) -->
    <slot>{{ loading ? loadingText : '' }}</slot>

    <!-- Icon (after text) - String name -->
    <BaseIcon v-if="icon && typeof icon === 'string' && iconAfter && !loading" :name="icon" :size="iconSize"
      :class="iconClasses" />

    <!-- Icon (after text) - Vue component -->
    <component v-else-if="icon && typeof icon !== 'string' && iconAfter && !loading" :is="icon"
      :class="[iconClasses, iconSizeClasses]" />
  </component>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import BaseLoading from './BaseLoading.vue'
import BaseIcon from './BaseIcon.vue'

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'success' | 'warning' | 'info' | 'outline-primary' | 'outline-secondary' | 'outline-danger'
type ButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl'

interface Props {
  variant?: ButtonVariant
  size?: ButtonSize
  loading?: boolean
  loadingText?: string
  disabled?: boolean
  icon?: string | any // Aceita tanto string (nome do ícone) quanto componente Vue
  iconAfter?: boolean
  tag?: 'button' | 'router-link' | 'a'
  type?: 'button' | 'submit' | 'reset'
  block?: boolean
  rounded?: boolean
  square?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'primary',
  size: 'md',
  loading: false,
  loadingText: 'Carregando...',
  disabled: false,
  iconAfter: false,
  tag: 'button',
  type: 'button',
  block: false,
  rounded: false,
  square: false
})

const emit = defineEmits<{
  click: [event: Event]
}>()

// Base classes that all buttons share
const baseClasses = [
  'inline-flex',
  'items-center',
  'justify-center',
  'font-semibold',
  'shadow-sm',
  'transition-all',
  'duration-200',
  'focus:outline-none',
  'focus:ring-2',
  'focus:ring-offset-2',
  'disabled:opacity-50',
  'disabled:cursor-not-allowed'
]

// Size-specific classes
const sizeClasses = {
  xs: ['px-2', 'py-1', 'text-xs'],
  sm: ['px-3', 'py-2', 'text-sm'],
  md: ['px-4', 'py-2.5', 'text-sm'],
  lg: ['px-6', 'py-3', 'text-base'],
  xl: ['px-8', 'py-4', 'text-lg']
}

// Variant-specific classes
const variantClasses = {
  primary: [
    'text-white',
    'bg-pdca-600',
    'border',
    'border-transparent',
    'hover:bg-pdca-700',
    'focus:ring-pdca-500'
  ],
  secondary: [
    'text-pdca-700',
    'bg-white',
    'border',
    'border-gray-300',
    'hover:bg-gray-50',
    'focus:ring-pdca-500'
  ],
  ghost: [
    'text-gray-700',
    'bg-transparent',
    'border',
    'border-transparent',
    'hover:bg-gray-100',
    'focus:ring-pdca-500'
  ],
  danger: [
    'text-white',
    'bg-red-600',
    'border',
    'border-transparent',
    'hover:bg-red-700',
    'focus:ring-red-500'
  ],
  success: [
    'text-white',
    'bg-green-600',
    'border',
    'border-transparent',
    'hover:bg-green-700',
    'focus:ring-green-500'
  ],
  warning: [
    'text-white',
    'bg-amber-600',
    'border',
    'border-transparent',
    'hover:bg-amber-700',
    'focus:ring-amber-500'
  ],
  info: [
    'text-white',
    'bg-blue-600',
    'border',
    'border-transparent',
    'hover:bg-blue-700',
    'focus:ring-blue-500'
  ],
  'outline-primary': [
    'text-pdca-700',
    'bg-transparent',
    'border-2',
    'border-pdca-600',
    'hover:bg-pdca-50',
    'focus:ring-pdca-500'
  ],
  'outline-secondary': [
    'text-gray-700',
    'bg-transparent',
    'border-2',
    'border-gray-300',
    'hover:bg-gray-50',
    'focus:ring-gray-500'
  ],
  'outline-danger': [
    'text-red-700',
    'bg-transparent',
    'border-2',
    'border-red-600',
    'hover:bg-red-50',
    'focus:ring-red-500'
  ]
}

const buttonClasses = computed(() => [
  ...baseClasses,
  ...sizeClasses[props.size],
  ...variantClasses[props.variant],
  {
    'w-full': props.block,
    'rounded-full': props.rounded,
    'rounded-lg': !props.rounded && !props.square,
    'rounded-none': props.square,
    'aspect-square': props.square,
    'p-2': props.square && props.size === 'sm',
    'p-2.5': props.square && props.size === 'md',
    'p-3': props.square && props.size === 'lg'
  }
])

const iconClasses = computed(() => [
  {
    'mr-2': !props.iconAfter && !props.loading && !props.square,
    'ml-2': props.iconAfter && !props.square
  }
])

const iconSize = computed(() => {
  switch (props.size) {
    case 'xs': return 'xs'
    case 'sm': return 'sm'
    case 'md': return 'md'
    case 'lg': return 'lg'
    case 'xl': return 'xl'
    default: return 'md'
  }
})

const iconSizeClasses = computed(() => {
  switch (props.size) {
    case 'xs': return 'w-3 h-3'
    case 'sm': return 'w-4 h-4'
    case 'md': return 'w-5 h-5'
    case 'lg': return 'w-6 h-6'
    case 'xl': return 'w-8 h-8'
    default: return 'w-5 h-5'
  }
})

const loadingSize = computed(() => {
  switch (props.size) {
    case 'xs': return 'xs'
    case 'sm': return 'sm'
    case 'md': return 'sm'
    case 'lg': return 'md'
    case 'xl': return 'lg'
    default: return 'sm'
  }
})

const loadingColor = computed(() => {
  const lightVariants = ['secondary', 'ghost', 'outline-primary', 'outline-secondary', 'outline-danger']
  return lightVariants.includes(props.variant) ? 'primary' : 'white'
})

function handleClick(event: Event) {
  if (!props.loading && !props.disabled) {
    emit('click', event)
  }
}
</script>
